import { NavLink, Outlet, Link } from "react-router-dom";
import { BarChart3, CreditCard, FolderTree, LayoutDashboard, LogOut, Megaphone, ScrollText, Settings2, Target, Users, ArrowLeft } from "lucide-react";
import { useAdminAuth } from "@/context/auth";

const NAV = [
  ["", "Dashboard", LayoutDashboard], ["analytics", "Analytics", BarChart3], ["users", "Users", Users],
  ["subscriptions", "Subscriptions", CreditCard], ["settings", "Plans & Banner", Settings2], ["broadcast", "Broadcasts", Megaphone],
  ["categories", "Categories", FolderTree], ["goals", "Goals", Target], ["audit-logs", "Audit Logs", ScrollText],
];

export function AdminTable({ cols, rows, onRow, testid, empty = "Nothing here yet." }) {
  return (
    <div className="t-card overflow-x-auto" data-testid={testid}>
      <table className="w-full text-[14px]">
        <thead><tr className="text-left text-[12px] uppercase tracking-wide text-[#64748B] border-b border-[#E2E8F0]">{cols.map((c) => <th key={c.label} className="px-4 py-3 font-semibold whitespace-nowrap">{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id ?? i} onClick={onRow ? () => onRow(r) : undefined} className={`border-b last:border-0 border-[#F1F5F9] ${onRow ? "cursor-pointer hover:bg-[#F8FAFC]" : ""}`} data-testid={testid ? `${testid}-row-${r.id ?? i}` : undefined}>
              {cols.map((c) => <td key={c.label} className="px-4 py-3 whitespace-nowrap">{c.render ? c.render(r) : String(r[c.k] ?? "—")}</td>)}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-[#64748B]">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export const Badge = ({ tone = "neutral", children }) => {
  const t = { success: "bg-[#ECFDF5] text-[#047857]", danger: "bg-[#FEF2F2] text-[#B91C1C]", brand: "bg-[#E5E5FD] text-[#0000EC]", warning: "bg-[#FFF7ED] text-[#C2410C]", neutral: "bg-[#F1F5F9] text-[#334155]" }[tone];
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12px] font-bold ${t}`}>{children}</span>;
};

export const StatCard = ({ label, value, accent = "#0000EC", testid }) => (
  <div className="t-card p-5" data-testid={testid}>
    <p className="text-[13px] font-semibold text-[#64748B]">{label}</p>
    <p className="text-[28px] font-extrabold mt-1" style={{ color: accent }}>{value ?? "—"}</p>
  </div>
);

export const AdminHeader = ({ title, sub, right }) => (
  <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
    <div><h1 className="text-[28px] lg:text-[32px] font-extrabold">{title}</h1>{sub && <p className="text-[14px] text-[#475569]">{sub}</p>}</div>{right}
  </div>
);

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  return (
    <div className="min-h-screen bg-[#F8FAFC] lg:flex">
      <aside className="lg:w-64 lg:h-screen lg:sticky lg:top-0 bg-white border-b lg:border-b-0 lg:border-r border-[#E2E8F0] p-4 lg:p-5 flex lg:flex-col gap-3" data-testid="admin-sidebar">
        <div className="hidden lg:block px-2 mb-6"><img src="/logo.png" alt="TOTO" className="w-10 h-10 rounded-xl mb-3" data-testid="admin-logo" /><p className="text-[22px] font-extrabold">TOTO <span className="text-[#0000EC]">Admin</span></p><p className="text-[12px] text-[#64748B] truncate">{admin?.email}</p></div>
        <nav className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar flex-1">
          {NAV.map(([to, label, Icon]) => (
            <NavLink key={to} to={`/admin${to ? `/${to}` : ""}`} end={!to} data-testid={`admin-nav-${to || "dashboard"}`}
              className={({ isActive }) => `flex items-center gap-3 h-10 px-3 rounded-xl text-[14px] whitespace-nowrap transition-colors ${isActive ? "bg-[#E5E5FD] text-[#0000EC] font-bold" : "text-[#475569] font-medium hover:bg-[#F1F5F9]"}`}>
              <Icon className="w-[18px] h-[18px]" />{label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex flex-col gap-1">
          <Link to="/" className="flex items-center gap-3 h-10 px-3 rounded-xl text-[14px] font-medium text-[#475569] hover:bg-[#F1F5F9]" data-testid="admin-back-to-app"><ArrowLeft className="w-[18px] h-[18px]" />Back to app</Link>
          <button onClick={logout} className="flex items-center gap-3 h-10 px-3 rounded-xl text-[14px] font-medium text-[#DC2626] hover:bg-[#FEF2F2]" data-testid="admin-logout"><LogOut className="w-[18px] h-[18px]" />Sign out</button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-5 lg:p-10 fade-up"><Outlet /></main>
    </div>
  );
}
